/**
 * Surveillance Members picker — Lab `surveillance.html` ui-select +
 * Backend-UI `WorkstationMemberSearch`.
 *
 * Live: `POST /v1/member/searchHierarchyNew` (100ms debounce, prefetch on empty).
 * Lab `refresh="$select.search"` re-queries on every keystroke; choices stay open
 * (including already-selected rows — Lab still lists them).
 */

import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Button, Field, Input, Spinner } from '@w7/ui'
import { surveillanceClient } from '@/api/surveillanceClient'

/** Lab ui-select `delay="100"`. */
const DEBOUNCE_MS = 100
const SUGGEST_LIMIT = 20

export type SurveillanceMemberSearchProps = {
  selected: string[]
  onAdd: (name: string) => void
}

export function SurveillanceMemberSearch({
  selected,
  onAdd,
}: SurveillanceMemberSearchProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [draft, setDraft] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(draft), DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [draft])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      const root = rootRef.current
      if (root && !root.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const membersQ = useQuery({
    queryKey: ['cm', 'surveillance', 'members', debounced],
    // Lab: filter = $select.search ("" on open, typed keyword while searching).
    queryFn: () => surveillanceClient.searchMembers(debounced),
    enabled: open,
    staleTime: 10_000,
    retry: false,
  })

  // Keep Lab parity: show API rows even if already selected (disabled pick).
  const suggestions = (membersQ.data?.usernames ?? []).slice(0, SUGGEST_LIMIT)
  const canAdd = draft.trim().length >= 1
  const selectedSet = new Set(selected)

  const pick = (name: string) => {
    if (selectedSet.has(name)) return
    onAdd(name)
    setDraft('')
    setOpen(true)
  }

  return (
    <div className="cm-surv-member-search" ref={rootRef}>
      <Field label="Members" className="cm-surv-field-members">
        {({ id, describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            className="cm-surv-member-input"
            placeholder="Enter or Select Member Name to Search"
            value={draft}
            aria-expanded={open}
            aria-controls="cm-surv-member-suggest-list"
            aria-autocomplete="list"
            aria-busy={membersQ.isFetching}
            onChange={(e) => {
              setDraft(e.target.value)
              setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (canAdd) pick(draft)
              }
              if (e.key === 'Escape') setOpen(false)
            }}
            suffix={
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="cm-surv-btn cm-surv-btn-in-input"
                disabled={!canAdd}
                title={canAdd ? 'Add member' : 'Type a member name to enable Add'}
                onClick={() => pick(draft)}
              >
                Add
              </Button>
            }
          />
        )}
      </Field>

      {open ? (
        <ul
          id="cm-surv-member-suggest-list"
          className="cm-surv-suggest"
          role="listbox"
          aria-label="Member suggestions"
        >
          {membersQ.isFetching && suggestions.length === 0 ? (
            <li className="cm-surv-suggest-status" role="presentation">
              <Spinner size={14} />
              <span>Searching downline…</span>
            </li>
          ) : null}
          {membersQ.isError ? (
            <li className="cm-surv-suggest-status" role="presentation">
              Could not load members — check CM-API / login.
            </li>
          ) : null}
          {suggestions.map((name) => {
            const already = selectedSet.has(name)
            return (
              <li key={name} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={already}
                  aria-disabled={already}
                  disabled={already}
                  className={already ? 'cm-surv-suggest-added' : undefined}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pick(name)
                  }}
                >
                  {name}
                  {already ? <span className="cm-surv-suggest-tag">added</span> : null}
                </button>
              </li>
            )
          })}
          {!membersQ.isFetching && suggestions.length === 0 && !membersQ.isError ? (
            <li className="cm-surv-suggest-status" role="presentation">
              No members match “{draft.trim() || '…'}”.
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  )
}
